import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { homepagePrimaryButtonClassName, homepageSectionContainerClassName } from '../constants/homepageUi';
import { submitDatasetRequest } from '../services/submitDatasetRequest';

const USE_CASE_OPTIONS = [
  { value: '', label: 'Select a use case' },
  { value: 'object-detection', label: 'Object Detection (real-world environments)' },
  { value: 'condition-state', label: 'Condition / State Detection (e.g. fresh, damaged, dirty, full/empty)' },
  { value: 'scene-environment', label: 'Scene / Environment Understanding (homes, kitchens, real-world spaces)' },
  { value: 'ocr-handwriting', label: 'OCR / Handwriting / Text Capture' },
  { value: 'video-data', label: 'Video Data (movement, interaction, multi-step tasks)' },
  { value: 'custom-dataset', label: 'Custom Dataset (describe in details)' },
];

const DATA_TYPE_OPTIONS = [
  { value: '', label: 'Select a data type' },
  { value: 'images-photo', label: 'Images (Photo)' },
  { value: 'video', label: 'Video' },
  { value: 'both', label: 'Both' },
];

const RequestDataset: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [useCase, setUseCase] = useState('');
  const [dataType, setDataType] = useState('');
  const [description, setDescription] = useState('');
  const [timeline, setTimeline] = useState('');
  const [budget, setBudget] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = 'Request a Dataset — Pixano';
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !description.trim() || !useCase || !dataType) return;
    setFormError('');
    setSubmitting(true);
    try {
      await submitDatasetRequest({
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        useCase,
        dataType,
        description: description.trim(),
        timeline: timeline.trim(),
        budget: budget.trim(),
      });
      setSubmitted(true);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'Something went wrong. Please try again or email support@pixano.ai.';
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#0B0F14]">
      <Navigation />
      <main className="flex flex-1 flex-col py-16 lg:py-20">
        <div className={homepageSectionContainerClassName}>
          <div className="mx-auto max-w-lg">
            {submitted ? (
              <div className="mb-8 flex justify-center sm:mb-10">
                <img
                  src="/pix-logo.png"
                  alt="Pixano"
                  className="h-40 w-40 object-contain sm:h-48 sm:w-48"
                  decoding="async"
                />
              </div>
            ) : null}
            <h1 className="mb-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">Request a Dataset</h1>
            <p className="mb-3 text-base leading-relaxed text-white/75 sm:text-lg">
              Tell us what you&apos;re trying to build — we&apos;ll match you with existing datasets or design a custom
              real-world data solution. You&apos;ll get relevant samples, options, and clear next steps.
            </p>
            <p className="mb-10 text-sm leading-relaxed text-white/45">
              Real-world, non-synthetic data captured and validated for production AI systems.
            </p>

            {submitted ? (
              <p className="text-lg font-medium text-white" role="status">
                Thanks — we&apos;ll reach out shortly with next steps.
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {formError ? (
                  <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm leading-relaxed text-red-200" role="alert">
                    {formError}
                  </p>
                ) : null}
                <div>
                  <label htmlFor="rd-name" className="mb-1.5 block text-sm font-medium text-white/80">
                    Name <span className="text-[#00e3ff]">*</span>
                  </label>
                  <input
                    id="rd-name"
                    name="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-lg border border-white/15 bg-[#121820] px-4 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-[#00e3ff]/50 focus:outline-none focus:ring-1 focus:ring-[#00e3ff]/30"
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label htmlFor="rd-email" className="mb-1.5 block text-sm font-medium text-white/80">
                    Email <span className="text-[#00e3ff]">*</span>
                  </label>
                  <input
                    id="rd-email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-white/15 bg-[#121820] px-4 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-[#00e3ff]/50 focus:outline-none focus:ring-1 focus:ring-[#00e3ff]/30"
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label htmlFor="rd-company" className="mb-1.5 block text-sm font-medium text-white/80">
                    Company
                  </label>
                  <input
                    id="rd-company"
                    name="company"
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full rounded-lg border border-white/15 bg-[#121820] px-4 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-[#00e3ff]/50 focus:outline-none focus:ring-1 focus:ring-[#00e3ff]/30"
                    placeholder="Company or team"
                    autoComplete="organization"
                  />
                </div>
                <div>
                  <label htmlFor="rd-usecase" className="mb-1.5 block text-sm font-medium text-white/80">
                    Use case <span className="text-[#00e3ff]">*</span>
                  </label>
                  <select
                    id="rd-usecase"
                    name="useCase"
                    required
                    value={useCase}
                    onChange={(e) => setUseCase(e.target.value)}
                    className="w-full rounded-lg border border-white/15 bg-[#121820] px-4 py-2.5 text-sm text-white focus:border-[#00e3ff]/50 focus:outline-none focus:ring-1 focus:ring-[#00e3ff]/30"
                  >
                    {USE_CASE_OPTIONS.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="rd-datatype" className="mb-1.5 block text-sm font-medium text-white/80">
                    Data Type <span className="text-[#00e3ff]">*</span>
                  </label>
                  <select
                    id="rd-datatype"
                    name="dataType"
                    required
                    value={dataType}
                    onChange={(e) => setDataType(e.target.value)}
                    className="w-full rounded-lg border border-white/15 bg-[#121820] px-4 py-2.5 text-sm text-white focus:border-[#00e3ff]/50 focus:outline-none focus:ring-1 focus:ring-[#00e3ff]/30"
                  >
                    {DATA_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value || 'empty-datatype'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="rd-description" className="mb-1.5 block text-sm font-medium text-white/80">
                    Description <span className="text-[#00e3ff]">*</span>
                  </label>
                  <textarea
                    id="rd-description"
                    name="description"
                    required
                    rows={5}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full resize-y rounded-lg border border-white/15 bg-[#121820] px-4 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-[#00e3ff]/50 focus:outline-none focus:ring-1 focus:ring-[#00e3ff]/30"
                    placeholder="What are you trying to build? (objects, environments, conditions, volume, formats, edge cases, etc.)"
                  />
                </div>
                <div>
                  <label htmlFor="rd-timeline" className="mb-1.5 block text-sm font-medium text-white/80">
                    Timeline
                  </label>
                  <input
                    id="rd-timeline"
                    name="timeline"
                    type="text"
                    value={timeline}
                    onChange={(e) => setTimeline(e.target.value)}
                    className="w-full rounded-lg border border-white/15 bg-[#121820] px-4 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-[#00e3ff]/50 focus:outline-none focus:ring-1 focus:ring-[#00e3ff]/30"
                    placeholder="e.g. Q2, flexible"
                  />
                </div>
                <div>
                  <label htmlFor="rd-budget" className="mb-1.5 block text-sm font-medium text-white/80">
                    Budget
                  </label>
                  <input
                    id="rd-budget"
                    name="budget"
                    type="text"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    className="w-full rounded-lg border border-white/15 bg-[#121820] px-4 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-[#00e3ff]/50 focus:outline-none focus:ring-1 focus:ring-[#00e3ff]/30"
                    placeholder="Optional"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`${homepagePrimaryButtonClassName} mt-2 w-full justify-center sm:w-auto disabled:opacity-50`}
                >
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RequestDataset;
